import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSecondaryMeasureAxisMaxValue,
  setSecondaryMeasureAxisMinValue,
  setUseSecondaryAxisForColumns,
  setUseSecondaryAxisForLines,
} from 'common/authoring_workflow/actions';
import {
  getUseSecondaryAxisForColumns,
  getUseSecondaryAxisForLines,
} from 'common/authoring_workflow/selectors/vifAuthoring';
import { AuthoringWorkflowState } from 'common/authoring_workflow/reducers/types';
import Tabs from '../../shared/Tabs';
import { I18n } from 'common/i18n';

const scope = 'shared.visualizations.panes.data.fields.dual_axis_options';
const columnTabIndex = 0;
const lineTabIndex = 1;

export const DualAxisOptions = () => {
  const vifAuthoring = useSelector((state: AuthoringWorkflowState) => state.vifAuthoring);
  const dispatch = useDispatch();
  const [tabIndex, setTabIndex] = useState(0);

  const onSetSecondaryMeasureAxisAutomatic = () => {
    dispatch(setSecondaryMeasureAxisMaxValue());
    dispatch(setSecondaryMeasureAxisMinValue());
  };

  const onRadioButtonChange = (useSecondaryAxis: boolean) => {
    if (tabIndex === columnTabIndex) {
      dispatch(setUseSecondaryAxisForColumns(useSecondaryAxis));
      if (!useSecondaryAxis && !getUseSecondaryAxisForLines(vifAuthoring)) {
        onSetSecondaryMeasureAxisAutomatic();
      }
    } else if (tabIndex === lineTabIndex) {
      dispatch(setUseSecondaryAxisForLines(useSecondaryAxis));
      if (!useSecondaryAxis && !getUseSecondaryAxisForColumns(vifAuthoring)) {
        onSetSecondaryMeasureAxisAutomatic();
      }
    }
  };

  const getSecondaryAxisChecked = () => {
    if (tabIndex === columnTabIndex) {
      return getUseSecondaryAxisForColumns(vifAuthoring);
    } else if (tabIndex === lineTabIndex) {
      return getUseSecondaryAxisForLines(vifAuthoring);
    }
    return false;
  };

  const renderAxisRadioButton = ({
    checked,
    className,
    id,
    onChange,
    title,
  }: {
    checked: boolean;
    className: string;
    id: string;
    onChange: () => void;
    title: string;
  }) => (
    <div className={className}>
      <input checked={checked} id={id} onChange={onChange} name="dual-axis-options" type="radio" />
      <label htmlFor={id}>
        <span className="fake-radiobutton" />
        <div className="translation-within-label">{title}</div>
      </label>
    </div>
  );

  const renderRadioButtons = () => {
    const secondaryAxisChecked = getSecondaryAxisChecked();
    return (
      <div className="authoring-field radiobutton">
        {renderAxisRadioButton({
          checked: !secondaryAxisChecked,
          className: 'primary-axis-container',
          id: 'dual-axis-primary',
          onChange: () => onRadioButtonChange(false),
          title: I18n.t('primary_axis', { scope: scope }),
        })}
        {renderAxisRadioButton({
          checked: secondaryAxisChecked,
          className: 'secondary-axis-container',
          id: 'dual-axis-secondary',
          onChange: () => onRadioButtonChange(true),
          title: I18n.t('secondary_axis', { scope: scope }),
        })}
      </div>
    );
  };

  const renderTabs = () => {
    const tabs = [
      {
        iconClassName: 'socrata-icon-bar-chart',
        onClickTab: () => setTabIndex(columnTabIndex),
        selected: tabIndex === columnTabIndex,
        tabIndex: columnTabIndex,
        title: I18n.t('column', { scope: scope }),
      },
      {
        iconClassName: 'socrata-icon-line-chart',
        onClickTab: () => setTabIndex(lineTabIndex),
        selected: tabIndex === lineTabIndex,
        tabIndex: lineTabIndex,
        title: I18n.t('line', { scope: scope }),
      },
    ];
    return <Tabs tabs={tabs} />;
  };

  return (
    <div className="dual-axis-container">
      {renderTabs()}
      {renderRadioButtons()}
    </div>
  );
};
